import { Clock } from 'lucide-react';

interface HoursDaysProps {
  startTime: string;
  endTime: string;
  setStartTime: (v: string) => void;
  setEndTime: (v: string) => void;
  activeDays: string[];
  toggleDay: (d: string) => void;
  title?: string;
  subtitle?: string;
  layout?: 'intake' | 'edit';
}

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DAY_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const ActiveHoursDaysStep = ({
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  activeDays,
  toggleDay,
  title = 'Active Hours & Days',
  subtitle = "Tell us when you're awake and active during the week.",
  layout = 'edit',
}: HoursDaysProps) => {
  const Days = (
    <div>
      <p className="text-deep-800 font-bold mb-3">Active days of the week</p>
      <div className="flex flex-wrap gap-2">
        {DAYS.map((d, i) => {
          const key = DAY_KEYS[i];
          const active = activeDays.includes(key);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggleDay(key)}
              className={`w-11 h-11 rounded-full font-bold text-base transition-colors ${
                active ? 'bg-purple-600 text-white' : 'bg-deep-100/60 text-deep-700'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );

  const Hours = (
    <div>
      <p className="text-deep-800 font-bold mb-3">Active tracking hours</p>
      <div className="flex gap-3">
        {[
          { label: 'FROM', value: startTime, set: setStartTime },
          { label: 'UNTIL', value: endTime, set: setEndTime },
        ].map((f) => (
          <div key={f.label} className="flex-1">
            <p className="text-xs text-deep-400 font-semibold mb-2">{f.label}</p>
            <div className="flex items-center gap-2 bg-white rounded-full px-4 py-3 shadow-sm">
              <Clock className="w-4 h-4 text-purple-600" />
              <input
                type="time"
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                className="flex-1 bg-transparent text-deep-800 font-semibold outline-none text-sm"
              />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-deep-400 mt-3">You won't get notifications outside these hours</p>
    </div>
  );

  return (
    <div className="space-y-7">
      <div>
        <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">{title}</h1>
        <p className="text-deep-500 mt-2 text-sm">{subtitle}</p>
      </div>
      {layout === 'intake' ? (
        <>
          {Days}
          {Hours}
        </>
      ) : (
        <>
          {Hours}
          {Days}
        </>
      )}
    </div>
  );
};

const REMINDERS = [
  { value: 45, label: '45 mins', tag: 'PRO', tagBg: 'bg-lime-500' },
  { value: 60, label: '60 mins', tag: 'AVG', tagBg: 'bg-deep-100' },
  { value: 90, label: '90 mins', tag: 'BEGINNER', tagBg: 'bg-deep-100' },
];

export const ReminderFrequencyStep = ({
  reminderInterval,
  setReminderInterval,
  title = 'Reminder Frequency',
  subtitle = 'How often should we nudge you?',
}: {
  reminderInterval: number;
  setReminderInterval: (v: number) => void;
  title?: string;
  subtitle?: string;
}) => (
  <div className="space-y-6">
    <div>
      {title && <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">{title}</h1>}
      {subtitle && <p className="text-deep-500 mt-2 text-sm">{subtitle}</p>}
    </div>
    <div className="space-y-3">
      {REMINDERS.map((r) => {
        const active = reminderInterval === r.value;
        return (
          <button
            key={r.value}
            type="button"
            onClick={() => setReminderInterval(r.value)}
            className={`w-full flex items-center justify-between rounded-full px-6 py-4 transition-all ${
              active
                ? 'bg-purple-600 text-white shadow-[0_4px_0_hsl(var(--deep-800))]'
                : 'bg-white text-deep-800 shadow-sm'
            }`}
          >
            <span className="font-bold text-lg">{r.label}</span>
            <span
              className={`text-[10px] font-bold tracking-wider px-3 py-1 rounded-full text-deep-800 ${
                active && r.value === 45 ? 'bg-lime-500' : r.tagBg
              }`}
            >
              {r.tag}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export const DailyGoalStep = ({
  recommendedGoal,
  dailyTarget,
  setDailyTarget,
  title = 'Daily Goal',
  subtitle = 'Set a goal that works for your activity level.',
  showUseRecommended = true,
}: {
  recommendedGoal: number;
  dailyTarget: string;
  setDailyTarget: (v: string) => void;
  title?: string;
  subtitle?: string;
  showUseRecommended?: boolean;
}) => (
  <div className="space-y-6">
    {(title || subtitle) && (
      <div>
        {title && <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">{title}</h1>}
        {subtitle && <p className="text-deep-500 mt-2 text-sm">{subtitle}</p>}
      </div>
    )}

    {Number(dailyTarget) !== recommendedGoal && (
      <div className="relative bg-lilac-300/60 rounded-2xl p-6 border-2 border-dashed border-purple-600/40">
        <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-lime-500 border-2 border-deep-800" />
        <div className="absolute bottom-4 right-4 w-7 h-7 rounded-full bg-lime-500 border-2 border-deep-800" />
        <div className="text-center relative">
          <p className="text-sm font-semibold text-deep-800">Your recommended daily goal</p>
          <p className="text-4xl font-extrabold text-purple-700 my-2 tracking-tight">
            {recommendedGoal} ML
          </p>
          <p className="text-xs text-deep-500">Auto-calculated based on active hours</p>
        </div>
      </div>
    )}

    <div>
      <p className="text-deep-800 font-bold mb-3">Your Daily Target (ml)</p>
      <p className="text-xs text-deep-500 mb-3">
        You can customize your daily goal here — feel free to adjust it during onboarding if the
        recommended amount doesn&apos;t fit your routine.
      </p>
      <div className="flex items-center bg-white rounded-full px-6 py-4 shadow-sm">
        <input
          type="number"
          value={dailyTarget}
          onChange={(e) => setDailyTarget(e.target.value)}
          className="flex-1 bg-transparent text-deep-800 font-bold text-lg outline-none"
        />
        <span className="text-deep-400 text-sm">ml</span>
      </div>
      <p className="text-xs text-deep-400 mt-2">Recommended: 500ml – 4000ml</p>
    </div>

    {showUseRecommended && (
      <button
        type="button"
        onClick={() => setDailyTarget(String(recommendedGoal))}
        className="w-full border-2 border-purple-600 text-purple-700 font-semibold rounded-full py-3"
      >
        Use Recommended Goal
      </button>
    )}
  </div>
);

export const NotifPrefsStep = ({
  achievement,
  setAchievement,
  sound,
  setSound,
  title = 'Notification Preferences',
  subtitle = 'Stay on track with the right notification settings.',
}: {
  achievement: boolean;
  setAchievement: (v: boolean) => void;
  sound: boolean;
  setSound: (v: boolean) => void;
  title?: string;
  subtitle?: string;
}) => {
  const Row = ({
    label,
    desc,
    checked,
    onChange,
  }: {
    label: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => (
    <div className="bg-lilac-300/60 rounded-2xl p-5 flex items-center justify-between gap-4">
      <div className="flex-1">
        <p className="font-bold text-deep-800">{label}</p>
        <p className="text-xs text-deep-500 mt-1">{desc}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-12 h-7 rounded-full p-0.5 transition-colors ${
          checked ? 'bg-purple-600' : 'bg-deep-200'
        }`}
      >
        <div
          className={`w-6 h-6 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-deep-800 leading-tight">{title}</h1>
        <p className="text-deep-500 mt-2 text-sm">{subtitle}</p>
      </div>
      <div className="space-y-4">
        <Row
          label="Achievement Notifications"
          desc="Get notified when you earn badges"
          checked={achievement}
          onChange={setAchievement}
        />
        <Row
          label="Sound Alerts"
          desc="Play sounds with notifications"
          checked={sound}
          onChange={setSound}
        />
      </div>
    </div>
  );
};
